import { useMemo } from 'react';

import type {
  Contest,
  AdditionalPayoutType,
} from '~/domains/contest/domains/services/interfaces/contest.interface';

import type { MyUserContest } from '../../myContests/types/userContest';

interface AdditionalPayoutSettingsResult {
  isEnabled: boolean;
  type: AdditionalPayoutType | null;
  deadline: Date | null;
  hasPrizeIncreased: boolean;
  originalPrizePool: number | null;
  currentPrizePool: number | null;
  message: string | null;
  showUpdatedPayout: boolean;
}

const getPayoutSettings = (contest: Contest | MyUserContest): AdditionalPayoutSettingsResult => {
  const settings = contest.additional_payout_settings;

  if (!settings) {
    return {
      isEnabled: false,
      type: null,
      deadline: null,
      hasPrizeIncreased: false,
      originalPrizePool: null,
      currentPrizePool: null,
      message: null,
      showUpdatedPayout: false,
    };
  }

  const type = settings.type;
  const currentPrizePool = contest.prize_pool || 0;
  const hasPrizeIncreased = currentPrizePool > settings.originalPrizePool;

  const showUpdatedPayout =
    contest.payout_schedule?.length === 1 &&
    settings.originalPrizePool !== null &&
    hasPrizeIncreased;

  return {
    isEnabled: settings.enabled,
    type,
    deadline: settings.deadline,
    hasPrizeIncreased,
    originalPrizePool: settings.originalPrizePool,
    currentPrizePool,
    message: settings.message,
    showUpdatedPayout,
  };
};

export const useAdditionalPayoutSettings = (
  contest: Contest | MyUserContest
): AdditionalPayoutSettingsResult => useMemo(() => getPayoutSettings(contest), [contest]);
